import React from 'react';
import {
    Box,
    Text,
    Collapse,
    ActionIcon,
    UnstyledButton,
} from '@mantine/core';
import { IconChevronDown, IconChevronUp, IconCopy } from '@tabler/icons-react';
import type { IExtractedNodeViewModel } from '@src/models';

interface IHierarchicalNodeProps {
    node: IExtractedNodeViewModel;
    depth: number;
    isExpanded: boolean;
    isSelected: boolean;
    expandedNodes: Set<string>;
    selectedNode?: IExtractedNodeViewModel | null;
    onToggle: (nodeId: string) => void;
    onSelect: (node: IExtractedNodeViewModel) => void;
    onCopy: (text?: string) => void;
}

const HierarchicalNode: React.FC<IHierarchicalNodeProps> = ({
    node,
    depth,
    isExpanded,
    isSelected,
    expandedNodes,
    selectedNode,
    onToggle,
    onSelect,
    onCopy,
}) => {
    const hasChildren = node.children && node.children.length > 0;
    const isLeaf = !!node.data;

    const shouldDisplayUom = (uom?: string | null): boolean => {
        return !!uom && uom !== "Not Found" && uom !== "Not Applicable";
    };

    const handleToggle = (e: React.MouseEvent) => {
        e.stopPropagation();
        onToggle(node.id);
    };

    const handleSelect = () => {
        if (isLeaf && node.data) {
            onSelect(node);
        } else if (hasChildren) {
            onToggle(node.id);
        }
    };

    const handleCopy = (e: React.MouseEvent) => {
        e.stopPropagation();
        onCopy(node.data?.value);
    };

    return (
        <>
            <Box
                className={isSelected ? 'selected' : ''}
                style={{ paddingLeft: depth * 16 }}
            >
                <UnstyledButton
                    onClick={handleSelect}
                    className={`text-item-button ${isSelected ? 'selected' : ''}`}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        width: '100%',
                        padding: '4px 8px',
                        borderRadius: 4,
                        backgroundColor: isSelected ? 'var(--mantine-color-brand-light)' : undefined,
                    }}
                >
                    <Box style={{ flex: 1 }}>
                        <Text
                            size={isLeaf ? "sm" : depth === 0 ? "lg" : "sm"}
                            c="dimmed"
                            fw={isLeaf ? 'normal' : 'bold'}
                        >
                            {isLeaf ? (
                                <>
                                    <span>{node.displayName}: </span>
                                    <span style={{ fontWeight: 'bold' }}>
                                        {node.data?.value}
                                        {shouldDisplayUom(node.data?.uom) && ` ${node.data?.uom}`}
                                    </span>
                                </>
                            ) : (
                                node.displayName
                            )}
                        </Text>
                    </Box>
                    {isLeaf && node.data && (
                        <ActionIcon component="span" size="sm" variant="subtle" onClick={handleCopy}>
                            <IconCopy size={14} />
                        </ActionIcon>
                    )}
                    {hasChildren && (
                        <ActionIcon component="span" size="sm" variant="subtle" onClick={handleToggle}>
                            {isExpanded ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />}
                        </ActionIcon>
                    )}
                </UnstyledButton>
            </Box>

            {hasChildren && (
                <Collapse in={isExpanded}>
                    <div>
                        {node.children?.map((child) => (
                            <MemoizedHierarchicalNode
                                key={child.id}
                                node={child}
                                depth={depth + 1}
                                isExpanded={expandedNodes.has(child.id)}
                                isSelected={selectedNode?.id === child.id}
                                expandedNodes={expandedNodes}
                                selectedNode={selectedNode}
                                onToggle={onToggle}
                                onSelect={onSelect}
                                onCopy={onCopy}
                            />
                        ))}
                    </div>
                </Collapse>
            )}
        </>
    );
};

const MemoizedHierarchicalNode = React.memo(HierarchicalNode);
export default MemoizedHierarchicalNode;
